/**
 * Account Model
 * 
 * Core data model for financial accounts in MoneyMojo
 * Following the data model specification
 */

import { AccountType, AssetCategory, AssetSubType, ValuationSource, ValueBehaviour, DepreciationMethod, LoanPurpose } from '../types/account.types';

export interface AccountModel {
  id: string;
  userId: string;
  name: string;
  type: AccountType;
  provider: string;
  accountNumber: string;
  openingBalance: number;
  currentBalance: number;
  openingDate: string;
  isActive: boolean;
  closureDate?: string;
  includeInNetWorth: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  
  // Type-specific fields
  creditLimit?: number;
  interestRate?: number;
  paymentDueDate?: string;
  renewalDate?: string;
  loanAmount?: number;
  lastPaymentDue?: string;
  interestCalculatedDay?: string;
  loanPurpose?: LoanPurpose;
  linkedAccountId?: string | null;
  leaseEndDate?: string;
  residualValue?: number;
  employerName?: string;
  
  // Asset-specific fields (when type === 'assets')
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
  // v2 fields (stored but not exposed in v1 UI)
  valueBehaviour?: ValueBehaviour;
  depreciationMethod?: DepreciationMethod;
  depreciationRatePercent?: number;
  autoUpdateValue?: boolean;
}

export class Account {
  private data: AccountModel;

  constructor(data: AccountModel) {
    this.data = { ...data };
  }

  // Getters
  get id(): string { return this.data.id; }
  get userId(): string { return this.data.userId; }
  get name(): string { return this.data.name; }
  get type(): AccountType { return this.data.type; }
  get provider(): string { return this.data.provider; }
  get accountNumber(): string { return this.data.accountNumber; }
  get openingBalance(): number { return this.data.openingBalance; }
  get currentBalance(): number { return this.data.currentBalance; }
  get openingDate(): string { return this.data.openingDate; }
  get isActive(): boolean { return this.data.isActive; }
  get closureDate(): string | undefined { return this.data.closureDate; }
  get includeInNetWorth(): boolean { return this.data.includeInNetWorth; }
  get notes(): string | undefined { return this.data.notes; }
  get createdAt(): string { return this.data.createdAt; }
  get updatedAt(): string { return this.data.updatedAt; }
  
  // Type-specific getters
  get creditLimit(): number | undefined { return this.data.creditLimit; }
  get interestRate(): number | undefined { return this.data.interestRate; }
  get paymentDueDate(): string | undefined { return this.data.paymentDueDate; }
  get renewalDate(): string | undefined { return this.data.renewalDate; }
  get loanAmount(): number | undefined { return this.data.loanAmount; }
  get lastPaymentDue(): string | undefined { return this.data.lastPaymentDue; }
  get interestCalculatedDay(): string | undefined { return this.data.interestCalculatedDay; }
  get loanPurpose(): LoanPurpose | undefined { return this.data.loanPurpose; }
  get linkedAccountId(): string | null | undefined { return this.data.linkedAccountId; }
  get leaseEndDate(): string | undefined { return this.data.leaseEndDate; }
  get residualValue(): number | undefined { return this.data.residualValue; }
  get employerName(): string | undefined { return this.data.employerName; }
  
  // Asset-specific getters
  get category(): AssetCategory | undefined { return this.data.category; }
  get subType(): AssetSubType | undefined { return this.data.subType; }
  get currentValue(): number | undefined { return this.data.currentValue; }
  get purchasePrice(): number | undefined { return this.data.purchasePrice; }
  get purchaseDate(): string | undefined { return this.data.purchaseDate; }
  get valuationSource(): ValuationSource | undefined { return this.data.valuationSource; }
  get valuationProvider(): string | undefined { return this.data.valuationProvider; }
  get lastValuationDate(): string | undefined { return this.data.lastValuationDate; }
  get valueBehaviour(): ValueBehaviour | undefined { return this.data.valueBehaviour; }
  get depreciationMethod(): DepreciationMethod | undefined { return this.data.depreciationMethod; }
  get depreciationRatePercent(): number | undefined { return this.data.depreciationRatePercent; }
  get autoUpdateValue(): boolean | undefined { return this.data.autoUpdateValue; }

  // Business logic methods
  isClosed(): boolean {
    return !this.data.isActive;
  }

  isCreditCard(): boolean {
    return this.data.type === AccountType.CREDIT_CARD;
  }

  isLoan(): boolean {
    return this.data.type === AccountType.LOAN;
  }

  isSavings(): boolean {
    return this.data.type === AccountType.SAVINGS;
  }

  isInvestment(): boolean {
    return this.data.type === AccountType.INVESTMENTS_SUPERANNUATION;
  }

  isSuperannuation(): boolean {
    return this.data.type === AccountType.INVESTMENTS_SUPERANNUATION;
  }

  isBankCash(): boolean {
    return this.data.type === AccountType.BANK_CASH;
  }

  isAsset(): boolean {
    return this.data.type === AccountType.ASSETS;
  }

  hasInterestRate(): boolean {
    return this.data.interestRate !== undefined && this.data.interestRate > 0;
  }

  hasCreditLimit(): boolean {
    return this.data.creditLimit !== undefined && this.data.creditLimit > 0;
  }

  hasLoanAmount(): boolean {
    return this.data.loanAmount !== undefined && this.data.loanAmount > 0;
  }

  // Update methods
  updateBalance(newBalance: number): void {
    this.data.currentBalance = newBalance;
    this.data.updatedAt = new Date().toISOString();
  }

  updateName(newName: string): void {
    this.data.name = newName;
    this.data.updatedAt = new Date().toISOString();
  }

  updateProvider(newProvider: string): void {
    this.data.provider = newProvider;
    this.data.updatedAt = new Date().toISOString();
  }

  updateNotes(newNotes: string): void {
    this.data.notes = newNotes;
    this.data.updatedAt = new Date().toISOString();
  }

  updateIncludeInNetWorth(include: boolean): void {
    this.data.includeInNetWorth = include;
    this.data.updatedAt = new Date().toISOString();
  }

  // Type-specific update methods
  updateCreditLimit(limit: number): void {
    if (this.isCreditCard()) {
      this.data.creditLimit = limit;
      this.data.updatedAt = new Date().toISOString();
    }
  }

  updateInterestRate(rate: number): void {
    this.data.interestRate = rate;
    this.data.updatedAt = new Date().toISOString();
  }

  updatePaymentDueDate(date: string): void {
    this.data.paymentDueDate = date;
    this.data.updatedAt = new Date().toISOString();
  }

  updateLoanAmount(amount: number): void {
    if (this.isLoan()) {
      this.data.loanAmount = amount;
      this.data.updatedAt = new Date().toISOString();
    }
  }

  // Account lifecycle methods
  close(closureDate: string, closureReason?: string): void {
    this.data.isActive = false;
    this.data.closureDate = closureDate;
    this.data.updatedAt = new Date().toISOString();
    if (closureReason) {
      this.data.notes = this.data.notes ? 
        `${this.data.notes}\nClosure reason: ${closureReason}` : 
        `Closure reason: ${closureReason}`;
    }
  }

  reopen(): void {
    this.data.isActive = true;
    this.data.closureDate = undefined;
    this.data.updatedAt = new Date().toISOString();
  }

  // Serialization methods
  toJSON(): AccountModel {
    return { ...this.data };
  }

  toPlainObject(): AccountModel {
    return { ...this.data };
  }

  // Static factory methods
  static create(data: Omit<AccountModel, 'id' | 'createdAt' | 'updatedAt'>): Account {
    const now = new Date().toISOString();
    const accountData: AccountModel = {
      ...data,
      id: crypto.randomUUID(),
      createdAt: now,
      updatedAt: now
    };
    return new Account(accountData);
  }

  static fromJSON(data: AccountModel): Account {
    return new Account(data);
  }

  // Validation methods
  validate(): string[] {
    const errors: string[] = [];

    if (!this.data.name || this.data.name.trim().length === 0) {
      errors.push('Account name is required');
    }

    if (!this.data.provider || this.data.provider.trim().length === 0) {
      errors.push('Provider is required');
    }

    if (!this.data.accountNumber || this.data.accountNumber.length !== 4) {
      errors.push('Account number must be exactly 4 digits');
    }

    if (isNaN(this.data.openingBalance)) {
      errors.push('Opening balance must be a valid number');
    }

    if (isNaN(this.data.currentBalance)) {
      errors.push('Current balance must be a valid number');
    }

    if (!this.data.openingDate || !this.isValidDate(this.data.openingDate)) {
      errors.push('Opening date must be a valid date');
    }

    // Type-specific validation
    if (this.isCreditCard()) {
      if (!this.data.creditLimit || this.data.creditLimit <= 0) {
        errors.push('Credit limit is required for credit card accounts');
      }
      if (!this.data.paymentDueDate || !this.isValidDate(this.data.paymentDueDate)) {
        errors.push('Payment due date is required for credit card accounts');
      }
    }

    if (this.isLoan()) {
      if (this.data.loanAmount === undefined || this.data.loanAmount === null || this.data.loanAmount < 0) {
        errors.push('Loan amount is required for loan accounts');
      }
      if (this.data.interestRate === undefined || this.data.interestRate === null || this.data.interestRate < 0) {
        errors.push('Interest rate is required for loan accounts');
      }
      if (!this.data.paymentDueDate || !this.isValidDate(this.data.paymentDueDate)) {
        errors.push('Payment due date is required for loan accounts');
      }
    }

    if (this.data.interestRate !== undefined && (this.data.interestRate < 0 || this.data.interestRate > 100)) {
      errors.push('Interest rate must be between 0 and 100');
    }

    // Asset-specific validation
    if (this.isAsset()) {
      if (!this.data.category) {
        errors.push('Category is required for asset accounts');
      }
      if (!this.data.subType) {
        errors.push('Sub-type is required for asset accounts');
      }
      if (this.data.currentValue === undefined || this.data.currentValue === null) {
        errors.push('Current value is required for asset accounts');
      }
      // Note: Negative values are allowed per FR-027 to represent liabilities or depreciated assets
      if (this.data.purchaseDate && !this.isValidDate(this.data.purchaseDate)) {
        errors.push('Purchase date must be a valid date');
      }
      if (this.data.purchaseDate && new Date(this.data.purchaseDate) > new Date()) {
        errors.push('Purchase date cannot be in the future');
      }
      if (this.data.lastValuationDate && !this.isValidDate(this.data.lastValuationDate)) {
        errors.push('Last valuation date must be a valid date');
      }
      if (this.data.depreciationRatePercent !== undefined && 
          (this.data.depreciationRatePercent < 0 || this.data.depreciationRatePercent > 100)) {
        errors.push('Depreciation rate must be between 0 and 100');
      }
    }

    return errors;
  }

  private isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
}
